/**
 * 트랙 재생성 모니터링 훅
 *
 * WebSocket을 통해 트랙 재생성 진행상황을 추적합니다.
 * TrackRegenerationMonitor 컴포넌트에서 분리된 상태 관리 로직입니다.
 */

import { useEffect, useState } from 'react';
import type { LogEntry } from '@/components/progress/GenerationLogs';
import {
  useWebSocket,
  type WebSocketMessage,
  type ProgressUpdate,
  type ErrorNotification,
  type LogMessage,
  type VersionCreated,
} from './websocket';

export interface UseTrackRegenerationOptions {
  trackId: string | number;
  onVersionCreated?: (message: VersionCreated) => void;
  onError?: (message: ErrorNotification) => void;
}

export interface UseTrackRegenerationReturn {
  /** WebSocket 연결 상태 */
  ws: ReturnType<typeof useWebSocket>;
  /** 진행률 (0-100) */
  progressPercent: number;
  /** 현재 단계 메시지 */
  currentStep: string;
  /** 로그 항목 목록 */
  logs: LogEntry[];
  /** 완료 여부 */
  isCompleted: boolean;
  /** 에러 발생 여부 */
  hasError: boolean;
  /** 생성된 버전 번호 */
  versionNumber: number | null;
}

export function useTrackRegeneration({
  trackId,
  onVersionCreated,
  onError,
}: UseTrackRegenerationOptions): UseTrackRegenerationReturn {
  const ws = useWebSocket({ autoConnect: true });

  const [progressPercent, setProgressPercent] = useState(0);
  const [currentStep, setCurrentStep] = useState('연결 중...');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [versionNumber, setVersionNumber] = useState<number | null>(null);

  // 로그 추가 헬퍼
  const addLog = (message: string, level: 'info' | 'warning' | 'error') => {
    setLogs((prev) => [
      ...prev,
      {
        id: new Date().toISOString(),
        message,
        level,
        timestamp: new Date().toISOString(),
      },
    ]);
  };

  // WebSocket 이벤트 핸들러
  const handleProgressUpdate = (message: WebSocketMessage) => {
    if (message.type !== 'progress_update') {return;}
    const update = message as ProgressUpdate;
    setProgressPercent(update.progressPercent);
    setCurrentStep(update.currentStep);
  };

  const handleErrorNotification = (message: WebSocketMessage) => {
    if (message.type !== 'error_notification') {return;}
    const error = message as ErrorNotification;
    setHasError(true);
    setCurrentStep(`에러: ${error.errorMessage}`);
    addLog(error.errorMessage, 'error');
    onError?.(error);
  };

  const handleLogMessage = (message: WebSocketMessage) => {
    if (message.type !== 'log_message') {return;}
    const log = message as LogMessage;
    addLog(log.message, log.level);
  };

  const handleVersionCreated = (message: WebSocketMessage) => {
    if (message.type !== 'version_created') {return;}
    const version = message as VersionCreated;
    setVersionNumber(version.versionNumber);
    setIsCompleted(true);
    setProgressPercent(100);
    setCurrentStep('버전 생성 완료');
    addLog(`새로운 버전 ${version.versionNumber}이 생성되었습니다.`, 'info');
    onVersionCreated?.(version);
  };

  // WebSocket 연결 및 이벤트 등록
  useEffect(() => {
    ws.connect(String(trackId), 'track');

    ws.on('progress_update', handleProgressUpdate);
    ws.on('error_notification', handleErrorNotification);
    ws.on('log_message', handleLogMessage);
    ws.on('version_created', handleVersionCreated);

    return () => {
      ws.off('progress_update', handleProgressUpdate);
      ws.off('error_notification', handleErrorNotification);
      ws.off('log_message', handleLogMessage);
      ws.off('version_created', handleVersionCreated);
      ws.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 콜백 함수들은 매 렌더마다 재생성되므로 trackId만 의존성으로 사용
  }, [trackId]);

  // 연결 상태 로그
  useEffect(() => {
    if (ws.status === 'connected') {
      addLog('WebSocket 연결됨', 'info');
      setCurrentStep('대기 중...');
    } else if (ws.status === 'connecting') {
      setCurrentStep('연결 중...');
    } else if (ws.status === 'error') {
      addLog('WebSocket 연결 에러', 'error');
    }
  }, [ws.status]);

  return {
    ws,
    progressPercent,
    currentStep,
    logs,
    isCompleted,
    hasError,
    versionNumber,
  };
}
